import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { AuthN, getConfig } from '@seek/online-identity';

import { getEnvironment, getLanguage } from 'src/core/config/globalStates';
import { getViewer } from 'src/services/userService';
import type { AppUserProfile } from 'src/types/userProfile';

const siteName = 'seekAnz-candidate';

export interface AuthUser {
  ID?: string;
  email?: string;
}

interface AuthClient {
  getToken: () => Promise<string | void>;
  getUserInfo: () => Promise<AuthUser>;
  login: (returnPath?: string) => void;
  isAuthenticated: () => Promise<boolean>;
}

export const getAuthClient = (language?: string): AuthClient => {
  if (typeof window === 'undefined') {
    throw new Error('AuthN is not initialized.');
  }

  return createAuthClient(language);
};

export const getAuthenticatedUserContext = async (
  token: string,
): Promise<AppUserProfile> => {
  try {
    const user = await getViewer(token);

    return user;
  } catch (error) {
    throw new Error(`Failed to get user. Error: ${error}`);
  }
};

const createAuthClient = (language?: string): AuthClient => {
  const authN = new AuthN(getConfig(siteName, window.location));
  const preferredLanguage = language ?? 'en';

  const login = async (returnPath?: string) => {
    const returnUri =
      returnPath ?? window.location.pathname + window.location.search;
    await authN.login({
      additionalArguments: { preferredLanguage },
      returnUri,
    });
  };

  return {
    getToken: async () => {
      const token = await authN.getToken();
      return token;
    },
    getUserInfo: async (): Promise<AuthUser> => {
      const identity = await authN.getIdentity();
      return {
        ID: identity?.sub?.ID,
        email: identity?.sub?.email,
      };
    },
    login,
    isAuthenticated: async () => {
      const isAuthenticated = await authN.isAuthenticated();
      return isAuthenticated;
    },
  };
};

export interface AuthenticationResult {
  authenticationStatus: AUTHENTICATION_TYPES;
  token?: string;
  userProfile?: AppUserProfile;
  login: (returnPath?: string) => void;
}

export const getAuthentication = async (
  skipUserProfile: boolean = false,
): Promise<AuthenticationResult> => {
  const environment = getEnvironment();
  const language = getLanguage();
  const authClient = getAuthClient(language);

  const login = authClient.login;

  const UnauthenticatedResult: AuthenticationResult = {
    authenticationStatus: AUTHENTICATION_TYPES.UNAUTHENTICATED,
    login,
  };

  const localToken = localStorage?.getItem('token');

  if (environment === 'development' && !localToken) {
    return UnauthenticatedResult;
  }

  if (environment !== 'production' && localToken) {
    const userProfile: AppUserProfile | undefined =
      localToken && !skipUserProfile
        ? await getAuthenticatedUserContext(localToken)
        : undefined;

    return {
      authenticationStatus: AUTHENTICATION_TYPES.AUTHENTICATED,
      token: localToken,
      userProfile,
      login,
    };
  }

  const isAuthenticated = await authClient.isAuthenticated();
  if (!isAuthenticated) {
    return UnauthenticatedResult;
  }

  const token = (await authClient.getToken()) || undefined;

  const userProfile: AppUserProfile | undefined =
    token && !skipUserProfile
      ? await getAuthenticatedUserContext(token)
      : undefined;

  return {
    authenticationStatus: AUTHENTICATION_TYPES.AUTHENTICATED,
    token,
    userProfile,
    login,
  };
};
